// Flatland Trap Story Compiler
// Compiles Twee code to inline HTML with site theme

export const compileFlatlandStory = (tweeCode) => {
  // Parse passages from Twee code
  const passages = [];
  const lines = tweeCode.split('\n');
  let currentPassage = null;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    if (line.startsWith(':: ') && !line.startsWith(':: StoryTitle') && !line.startsWith(':: StoryData')) {
      if (currentPassage) {
        passages.push(currentPassage);
      }
      const passageName = line.substring(3).trim();
      currentPassage = {
        name: passageName,
        content: '',
        id: passageName.replace(/\s+/g, '-')
      };
    } else if (currentPassage && line.trim()) {
      currentPassage.content += line + '\n';
    }
  }
  
  if (currentPassage) {
    passages.push(currentPassage);
  }
  
  // Process content formatting
  const processContent = (text) => {
    let processed = text;
    
    // SugarCube variables - replace with spans for display (these will be updated by JS)
    processed = processed.replace(/\$skepticism/g, '<span class="var-skepticism">100</span>');
    processed = processed.replace(/\$knowledge/g, '<span class="var-knowledge">0</span>');
    
    // Horizontal rules
    processed = processed.replace(/^---$/gm, '<hr class="flatland-divider">');
    
    // Bold: **text**
    processed = processed.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    
    // Italic: *text* or //text//
    processed = processed.replace(/(?<!<strong>)\*([^*\n]+)\*(?!<\/strong>)/g, '<em>$1</em>');
    processed = processed.replace(/\/\/([^/]+)\/\//g, '<em>$1</em>');
    
    // Headings: !Heading
    processed = processed.replace(/^!+\s*(.+)$/gm, (match, text) => {
      return `<h3 class="flatland-heading">${text}</h3>`;
    });
    
    // Line breaks
    processed = processed.replace(/\n\n/g, '</p><p>');
    processed = '<p>' + processed + '</p>';
    
    return processed;
  };
  
  // Generate passage HTML
  let passagesHtml = passages.map(passage => {
    const linkRegex = /\[\[([^\]]+)\]\]/g;
    const links = [];
    let match;
    
    while ((match = linkRegex.exec(passage.content)) !== null) {
      const linkText = match[1];
      let label, target, effect = 'none';
      
      if (linkText.includes('|')) {
        const parts = linkText.split('|');
        label = parts[0].trim();
        target = parts[1].trim();
      } else {
        label = linkText.trim();
        target = linkText.trim();
      }
      
      // Check for effects
      if (label.includes('{{{pulse}}}')) {
        effect = 'pulse';
        label = label.replace('{{{pulse}}}', '').trim();
      } else if (label.includes('{{{highlight}}}')) {
        effect = 'highlight';
        label = label.replace('{{{highlight}}}', '').trim();
      } else if (label.includes('{{{glow}}}')) {
        effect = 'glow';
        label = label.replace('{{{glow}}}', '').trim();
      }
      
      const isExternalLink = target.startsWith('URL:');
      const finalTarget = isExternalLink ? target.substring(4) : target.replace(/\s+/g, '-');
      links.push({ label, target: finalTarget, isExternal: isExternalLink, effect });
    }
    
    // Remove link syntax from content for processing
    let content = passage.content.replace(linkRegex, '');
    
    // Remove SugarCube set commands from content (they'll be executed via data attributes)
    const setRegex = /<<set\s+\$(\w+)\s+to\s+(.+?)>>\s*/g;
    const setCommands = [];
    
    // Extract set commands
    content = content.replace(setRegex, (match, varName, value) => {
      setCommands.push({ varName, value });
      return ''; // Remove from visible content
    });
    
    content = processContent(content.trim());
    
    // Add set commands as hidden spans at the end
    const commandsHtml = setCommands.map(cmd => 
      `<span class="sugar-command" data-command="set" data-var="${cmd.varName}" data-value="${cmd.value}"></span>`
    ).join('');
    
    content = commandsHtml + content;
    
    // Generate link buttons
    const linksHtml = links.map(link => {
      const effectClass = link.effect !== 'none' ? ` link-effect-${link.effect}` : '';
      if (link.isExternal) {
        return `<a href="${link.target}" class="flatland-link${effectClass}" target="_blank" rel="noopener noreferrer">${link.label}</a>`;
      }
      return `<button class="flatland-link${effectClass}" onclick="flatlandNavigate('${link.target}')">${link.label}</button>`;
    }).join('');
    
    const isStart = passage.name === 'Start';
    
    return `
      <div class="flatland-passage" id="passage-${passage.id}" data-name="${passage.name}"${isStart ? ' data-start="true"' : ''}>
        <div class="flatland-content">${content}</div>
        ${linksHtml ? `<div class="flatland-links">${linksHtml}</div>` : ''}
      </div>
    `;
  }).join('');
  
  // Generate complete HTML with CSS and JavaScript
  return `
    <style>
      .flatland-story-container {
        background: white;
        color: #2d3748;
        border-radius: 12px;
        padding: 24px;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        line-height: 1.8;
      }
      
      .flatland-passage {
        display: none;
        animation: flatlandFadeIn 0.3s ease-in;
      }
      
      .flatland-passage.active {
        display: block;
      }
      
      @keyframes flatlandFadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      
      .flatland-content {
        margin-bottom: 24px;
      }
      
      .flatland-content p {
        margin-bottom: 1.5rem;
        color: #2d3748;
      }
      
      .flatland-content strong {
        color: #fb923c;
        font-weight: 600;
      }
      
      .flatland-content em {
        color: #64748b;
        font-style: italic;
      }
      
      .flatland-heading {
        color: #fb923c;
        font-size: 1.1em;
        font-weight: 600;
        margin: 1.5rem 0 1rem 0;
      }
      
      .flatland-divider {
        border: none;
        border-top: 2px solid #fb923c;
        margin: 1.5rem 0;
        opacity: 0.3;
      }
      
      .flatland-links {
        display: flex;
        flex-direction: column;
        gap: 12px;
        margin-top: 24px;
      }
      
      .flatland-link {
        display: block;
        width: 100%;
        padding: 14px 20px;
        background: linear-gradient(135deg, #fb923c, #f97316);
        color: white;
        border: none;
        border-radius: 8px;
        font-size: 1rem;
        font-weight: 600;
        text-align: center;
        text-decoration: none;
        cursor: pointer;
        transition: all 0.3s ease;
        box-shadow: 0 2px 4px rgba(251, 146, 60, 0.2);
      }
      
      .flatland-link:hover {
        background: linear-gradient(135deg, #f97316, #ea580c);
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(251, 146, 60, 0.3);
      }
      
      /* Link Effects */
      @keyframes flatlandPulse {
        0%, 100% { 
          opacity: 1; 
          transform: scale(1);
        }
        50% { 
          opacity: 0.9; 
          transform: scale(1.03);
        }
      }
      
      @keyframes flatlandGlow {
        0%, 100% { 
          box-shadow: 0 2px 4px rgba(251, 146, 60, 0.2);
        }
        50% { 
          box-shadow: 0 0 20px rgba(251, 146, 60, 0.8), 0 0 30px rgba(251, 146, 60, 0.6);
        }
      }
      
      .link-effect-pulse {
        animation: flatlandPulse 2s ease-in-out infinite;
      }
      
      .link-effect-highlight {
        background: linear-gradient(135deg, #fbbf24, #f59e0b);
        box-shadow: 0 4px 12px rgba(251, 191, 36, 0.5);
      }
      
      .link-effect-glow {
        animation: flatlandGlow 2s ease-in-out infinite;
      }
      
      .sugar-command {
        display: none;
      }
      
      /* Variable display styling */
      .var-skepticism,
      .var-knowledge {
        color: #fb923c;
        font-weight: 600;
      }
    </style>
    
    <div class="flatland-story-container">
      <div id="flatland-passages">
        ${passagesHtml}
      </div>
    </div>
    
    <script>
      (function() {
        let skepticism = 100;
        let knowledge = 0;
        let history = [];
        let currentPassage = 'Start';
        
        function updateStats() {
          // Update all variable displays
          const skepticismElements = document.querySelectorAll('.var-skepticism');
          const knowledgeElements = document.querySelectorAll('.var-knowledge');
          
          skepticismElements.forEach(el => {
            el.textContent = skepticism;
          });
          
          knowledgeElements.forEach(el => {
            el.textContent = knowledge;
          });
        }
        
        function showPassage(passageName, shouldScroll = true) {
          const passages = document.querySelectorAll('.flatland-passage');
          passages.forEach(p => p.classList.remove('active'));
          
          const targetId = 'passage-' + passageName;
          const target = document.getElementById(targetId);
          
          if (target) {
            target.classList.add('active');
            currentPassage = passageName;
            history.push(passageName);
            
            // Execute set commands in this passage
            const commands = target.querySelectorAll('.sugar-command[data-command="set"]');
            commands.forEach(cmd => {
              const varName = cmd.dataset.var;
              const value = cmd.dataset.value;
              
              if (varName === 'skepticism') {
                if (value.includes('$skepticism')) {
                  const operation = value.match(/\$skepticism\s*([+\-])\s*(\d+)/);
                  if (operation) {
                    const operator = operation[1];
                    const amount = parseInt(operation[2]);
                    if (operator === '+') skepticism += amount;
                    else if (operator === '-') skepticism -= amount;
                  }
                } else {
                  skepticism = parseInt(value);
                }
              } else if (varName === 'knowledge') {
                if (value.includes('$knowledge')) {
                  const operation = value.match(/\$knowledge\s*([+\-])\s*(\d+)/);
                  if (operation) {
                    const operator = operation[1];
                    const amount = parseInt(operation[2]);
                    if (operator === '+') knowledge += amount;
                    else if (operator === '-') knowledge -= amount;
                  }
                } else {
                  knowledge = parseInt(value);
                }
              }
            });
            
            updateStats();
            
            // Scroll for user-triggered navigation, not initial page setup
            if (shouldScroll) {
              target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
          }
        }
        
        window.flatlandNavigate = function(passageName) {
          showPassage(passageName);
        };
        
        window.flatlandReset = function() {
          skepticism = 100;
          knowledge = 0;
          history = [];
          showPassage('Start');
        };
        
        window.flatlandGoBack = function() {
          if (history.length > 1) {
            history.pop(); // Remove current
            const previousPassage = history[history.length - 1];
            history.pop(); // Remove it so showPassage will add it back
            showPassage(previousPassage);
          }
        };
        
        // Initialize without moving the host page away from its top
        showPassage('Start', false);
      })();
    </script>
  `;
};
